<template>
    <div class="search-result-item" @click="$emit('select', result)">
        <div class="segment-content">
            <div class="text-block left">
                <span class="text" v-html="highlightedSource"></span>
            </div>
            <div class="text-block right">
                <span class="text" v-html="highlightedTarget"></span>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';
    import { SegmentSearchResultItem, SearchField } from './SegmentSearchModels';

    export default defineComponent({
        name: 'SearchResultItem',
        props: {
            result: {
                type: Object as PropType<SegmentSearchResultItem>,
                required: true
            },
            searchText: {
                type: String,
                required: true
            },
            searchField: {
                type: Number as PropType<SearchField>,
                required: true
            }
        },
        emits: ['select'],
        computed: {
            highlightedSource(): string {
                if (this.searchField === SearchField.Source) {
                    return this.highlightText(this.result.sourceText);
                }
                return this.escapeHtml(this.result.sourceText);
            },
            highlightedTarget(): string {
                if (this.searchField === SearchField.Target) {
                    return this.highlightText(this.result.targetText);
                }
                return this.escapeHtml(this.result.targetText);
            }
        },
        methods: {
            escapeHtml(text: string): string {
                const div = document.createElement('div');
                div.textContent = text;
                return div.innerHTML;
            },
            highlightText(text: string): string {
                if (!this.searchText) {
                    return this.escapeHtml(text);
                }

                const escapedSearch = this.searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const regex = new RegExp(`(${escapedSearch})`, 'gi');
                const parts = text.split(regex);

                return parts
                    .map(part => {
                        if (part.toLowerCase() === this.searchText.toLowerCase()) {
                            return `<mark class="highlight">${this.escapeHtml(part)}</mark>`;
                        }
                        return this.escapeHtml(part);
                    })
                    .join('');
            }
        }
    });
</script>

<style lang="scss" scoped>
    .search-result-item {
        padding: 12px;
        cursor: pointer;
        transition: background-color 0.2s ease;

        &:hover {
            background-color: var(--dark-blue);
        }

        &:last-child {
            border-bottom: none;
        }
    }

    .segment-content {
        display: flex;
        gap: 16px;
        font-size: 14px;
        padding: 10px;
        background-color: var(--light-blue);
    }

    .text-block {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
        background-color: var(--light-blue);

        &.left {
            border-right: 1px solid var(--el-border-color-light);
            padding-right: 16px;
        }

        &.right {
            padding-left: 0;
        }
    }

    .label {
        color: var(--text-color);
        opacity: 0.7;
        font-size: 12px;
    }

    .text {
        color: var(--text-color);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        :deep(.highlight) {
            background-color: var(--yellow);
            padding: 0 2px;
            border-radius: 2px;
        }
    }
</style>
